<template>
  <transition name="modal-fade">
    <div v-if="modelValue" class="modal-overlay" @click.self="closeModal">
      <div class="modal-container">
        <div class="modal-header">
          <h2 class="modal-title">
            <IconSvg v-if="isCopyMode" name="copy-solid-full" size="md" />
            <IconSvg v-else name="gear-solid-full" size="md" />
            {{ isCopyMode ? "しおりを複製" : "しおりの管理" }}
          </h2>
          <button
            type="button"
            class="modal-close-btn"
            @click="closeModal"
            :disabled="isSubmitting"
          >
            ×
          </button>
        </div>

        <!-- エラーメッセージ表示 -->
        <div v-if="errorMessage" class="error-message">
          {{ errorMessage }}
        </div>

        <!-- 成功メッセージ表示 -->
        <div v-if="successMessage" class="success-message">
          {{ successMessage }}
        </div>

        <form @submit.prevent="handleUpdate" class="modal-form">
          <div class="form-group">
            <label class="form-label">
              タイトル <span class="required">*</span>
            </label>
            <input
              v-model="formData.title"
              type="text"
              class="form-input"
              :class="{ 'input-error': errors.title }"
              placeholder="例: 長野_上高地"
              maxlength="255"
              required
            />
            <p v-if="errors.title" class="error-text">
              {{ errors.title }}
            </p>
          </div>

          <div class="form-group">
            <label class="form-label">
              開始日 <span class="required">*</span>
            </label>
            <input
              v-model="formData.startDate"
              type="date"
              class="form-input"
              :class="{ 'input-error': errors.startDate }"
              min="1900-01-01"
              max="9999-12-31"
              @input="validateYear('startDate')"
              @change="validateYear('startDate')"
              @blur="validateYear('startDate')"
              required
            />
            <p v-if="errors.startDate" class="error-text">
              {{ errors.startDate }}
            </p>
          </div>

          <div class="form-group">
            <label class="form-label">
              終了日 <span class="required">*</span>
            </label>
            <input
              v-model="formData.endDate"
              type="date"
              class="form-input"
              :class="{ 'input-error': errors.endDate }"
              min="1900-01-01"
              max="9999-12-31"
              @input="validateYear('endDate')"
              @change="validateYear('endDate')"
              @blur="validateYear('endDate')"
              required
            />
            <p v-if="errors.endDate" class="error-text">
              {{ errors.endDate }}
            </p>
          </div>

          <!-- 予算 -->
          <div class="form-group">
            <label class="form-label">予算（円）</label>
            <input
              v-model.number="formData.budget"
              type="number"
              class="form-input"
              min="0"
              step="1000"
              placeholder="例: 100000"
            />
          </div>

          <!-- 渡航先選択 -->
          <div class="form-group">
            <label class="form-label">
              旅行先 <span class="required">*</span>
            </label>
            <div class="destination-type-selector">
              <button
                type="button"
                class="destination-type-btn"
                :class="{ active: formData.destinationType === 'domestic' }"
                @click="formData.destinationType = 'domestic'"
                :disabled="isSubmitting"
              >
                JP 国内
              </button>
              <button
                type="button"
                class="destination-type-btn"
                :class="{
                  active: formData.destinationType === 'international',
                }"
                @click="formData.destinationType = 'international'"
                :disabled="isSubmitting"
              >
                <IconSvg name="earth-americas-solid-full" size="sm" /> 海外
              </button>
            </div>

            <!-- 渡航先入力エリア -->
            <div class="destinations-container">
              <div
                v-for="(destination, index) in formData.destinations"
                :key="index"
                class="destination-input-row"
              >
                <div class="destination-autocomplete">
                  <input
                    v-model="destination.value"
                    @input="handleDestinationInput(index)"
                    @focus="showSuggestions(index)"
                    @blur="hideSuggestions(index)"
                    type="text"
                    class="form-input"
                    :placeholder="
                      formData.destinationType === 'domestic'
                        ? '都道府県名を入力'
                        : '国名を入力'
                    "
                    :disabled="isSubmitting"
                  />
                  <!-- 候補リスト -->
                  <div
                    v-if="
                      destination.showSuggestions &&
                      destination.filteredList.length > 0
                    "
                    class="suggestions-list"
                  >
                    <div
                      v-for="(suggestion, sIndex) in destination.filteredList"
                      :key="sIndex"
                      class="suggestion-item"
                      @mousedown.prevent="selectDestination(index, suggestion)"
                    >
                      {{ suggestion }}
                    </div>
                  </div>
                </div>
                <button
                  v-if="formData.destinations.length > 1"
                  type="button"
                  @click="removeDestination(index)"
                  class="remove-member-btn"
                  title="削除"
                  :disabled="isSubmitting"
                >
                  ×
                </button>
              </div>
              <button
                type="button"
                @click="addDestination"
                class="add-member-btn"
                :disabled="formData.destinations.length >= 10 || isSubmitting"
              >
                ＋ 旅行先を追加
              </button>
            </div>
            <p v-if="errors.destinations" class="error-text">
              {{ errors.destinations }}
            </p>
          </div>

          <div v-if="isCopyMode || canManage" class="form-group">
            <label class="form-label">
              メンバー <span class="required">*</span>
            </label>
            <div class="member-add-tools">
              <button
                type="button"
                class="friend-list-toggle"
                @click="toggleFriendList"
              >
                <IconSvg name="user-solid-full" size="sm" />
                友達リストから追加
              </button>
            </div>
            <div v-if="isFriendListOpen" class="friend-picker">
              <input
                v-model.trim="friendSearch"
                type="search"
                class="form-input friend-search-input"
                placeholder="名前またはメールで検索"
                @input="fetchFriendCandidates"
              />
              <div v-if="friendCandidates.length === 0" class="friend-empty">
                追加できるユーザーが見つかりません
              </div>
              <button
                v-for="friend in friendCandidates"
                :key="friend.APP_USER_CODE"
                type="button"
                class="friend-option"
                :disabled="isFriendSelected(friend)"
                @click="addFriendMember(friend)"
              >
                <span class="friend-avatar">
                  <img
                    v-if="friend.PROFILE_IMAGE_BASE64"
                    :src="friend.PROFILE_IMAGE_BASE64"
                    :alt="friend.USER_NAME"
                    loading="lazy"
                    decoding="async"
                  />
                  <IconSvg v-else name="user-solid-full" size="sm" />
                </span>
                <span>
                  <strong>{{ friend.USER_NAME }}</strong>
                  <small>{{ friend.EMAIL }}</small>
                </span>
                <span class="friend-add-state">
                  {{ isFriendSelected(friend) ? "追加済み" : "追加" }}
                </span>
              </button>
            </div>
            <div class="members-input-container">
              <div
                v-for="(member, index) in formData.members"
                :key="member.member_code || `new-${index}`"
                class="member-input-row"
              >
                <input
                  v-model="member.member_name"
                  type="text"
                  class="form-input member-input"
                  :class="{
                    'input-error': errors.members && errors.members[index],
                  }"
                  :placeholder="`メンバー${index + 1}の名前`"
                  maxlength="255"
                  :readonly="Boolean(member.app_user_code)"
                />
                <button
                  v-if="
                    (isCopyMode ||
                      canDeleteMembers ||
                      (canManage && !member.member_code)) &&
                    formData.members.length > 1 &&
                    !isCurrentUser(member)
                  "
                  type="button"
                  @click="removeMember(index, member.member_code)"
                  class="remove-member-btn"
                  :title="`${member.member_name || `メンバー${index + 1}`}を削除`"
                  :aria-label="`${member.member_name || `メンバー${index + 1}`}を削除`"
                  :disabled="isSubmitting"
                >
                  ×
                </button>
              </div>
              <button
                type="button"
                @click="addMember"
                class="add-member-btn"
                :disabled="formData.members.length >= 20 || isSubmitting"
              >
                ＋ メンバーを追加
              </button>
            </div>
            <p
              v-if="errors.members && typeof errors.members === 'string'"
              class="error-text"
            >
              {{ errors.members }}
            </p>
          </div>

          <div v-if="!isCopyMode" class="form-group">
            <label class="form-label">旅行の写真</label>
            <div class="image-upload-area">
              <input
                ref="imageInput"
                type="file"
                accept="image/*,.jpg,.jpeg,.png,.gif,.webp,.heic,.heif,.hif"
                @change="handleImageSelect"
                class="image-input"
                id="manage-image-upload"
              />
              <!-- 既存の画像がある場合 -->
              <div
                v-if="currentImageUrl && !selectedImage"
                class="image-preview-container"
              >
                <img
                  :src="currentImageUrl"
                  alt="現在の画像"
                  class="image-preview"
                  loading="lazy"
                  decoding="async"
                />
                <button
                  type="button"
                  @click="openImagePicker"
                  class="change-image-btn"
                  :disabled="isSubmitting"
                >
                  画像を変更
                </button>
                <button
                  v-if="canWrite"
                  type="button"
                  @click="handleDeleteImage"
                  class="remove-image-btn"
                  :disabled="isSubmitting"
                  title="画像を削除"
                >
                  <IconSvg name="trash-solid-full" size="sm" />
                </button>
              </div>
              <!-- 新しい画像が選択された場合 -->
              <div v-else-if="imagePreview" class="image-preview-container">
                <img
                  :src="imagePreview"
                  alt="プレビュー"
                  class="image-preview"
                  decoding="async"
                />
                <button
                  type="button"
                  @click="openImagePicker"
                  class="change-image-btn"
                  :disabled="isSubmitting"
                >
                  別の画像を選択
                </button>
                <button
                  type="button"
                  @click="removeImage"
                  class="remove-image-btn"
                  title="選択を解除"
                >
                  ×
                </button>
              </div>
              <!-- 画像がない場合 -->
              <div v-else>
                <label
                  for="manage-image-upload"
                  class="image-upload-label"
                  @click.prevent="openImagePicker"
                >
                  <span class="upload-icon">
                    <IconSvg name="camera-solid-full" size="lg" />
                  </span>
                  <span class="upload-text">画像を選択</span>
                  <span class="upload-hint"
                    >HEIC対応・最大10MB</span
                  >
                </label>
              </div>
            </div>
            <!-- 新しい画像が選択されている場合はアップロードボタンを表示 -->
            <button
              v-if="selectedImage"
              type="button"
              @click="handleUploadImage"
              class="btn btn-upload"
              :disabled="isSubmitting"
            >
              {{ isSubmitting ? "アップロード中..." : "画像をアップロード" }}
            </button>
            <p v-if="errors.image" class="error-text">
              {{ errors.image }}
            </p>
          </div>

          <div class="modal-footer">
            <button
              v-if="!isCopyMode && canDelete"
              type="button"
              @click="handleDelete"
              class="btn btn-danger"
              :disabled="isSubmitting"
            >
              <span><IconSvg name="trash-solid-full" size="sm" /></span>
              <span>削除</span>
            </button>
            <div class="button-group">
              <button
                type="button"
                @click="closeModal"
                class="btn btn-secondary"
                :disabled="isSubmitting"
              >
                キャンセル
              </button>
              <button
                type="submit"
                class="btn btn-primary"
                :disabled="isSubmitting"
              >
                {{
                  isSubmitting
                    ? isCopyMode
                      ? "複製中..."
                      : "更新中..."
                    : isCopyMode
                    ? "複製"
                    : "更新"
                }}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  </transition>

  <!-- 共有モーダル -->
  <ShareShioriModal
    v-if="modelValue && shioriData"
    v-model="showShareModal"
    :shiori-id="shioriData.SHIORI_NOV"
    :can-manage="canManage"
  />
</template>

<script>
import { ref, watch, onMounted, onUnmounted } from "vue";
import { useRouter } from "vue-router";
import IconSvg from "@/components/IconSvg.vue";
import { useShioriStore } from "@/store/shiori-store.js";
import { useShioriMemberStore } from "@/store/shiori-member-store.js";
import { useScheduleStore } from "@/store/schedule-store.js";
import { useScheduleMemberStore } from "@/store/schedule-member-store.js";
import { useChecklistDetailStore } from "@/store/checklist-detail-store.js";
import { useAuthStore } from "@/store/authStore.js";
import { useDestinationMasterStore } from "@/store/destination-master-store.js";
import { filterDestinationSuggestions } from "@/data/destinationSuggestions.js";
import ShareShioriModal from "./ShareShioriModal.vue";
import axios from "axios";
import { API_URL } from "@/config/api.js";
import { isGuestMode } from "@/utils/guest-session";
import {
  fetchAuthenticatedShioriImageUrl,
  resolveEmbeddedShioriImageUrl,
  revokeShioriImageUrl,
} from "@/utils/shiori-image.js";

export default {
  name: "ManageShioriModal",
  components: {
    ShareShioriModal,
    IconSvg,
  },
  props: {
    modelValue: {
      type: Boolean,
      required: true,
    },
    shioriData: {
      type: Object,
      default: null,
    },
    isCopyMode: {
      type: Boolean,
      default: false,
    },
    canDelete: {
      type: Boolean,
      default: true,
    },
    canWrite: {
      type: Boolean,
      default: true,
    },
    canManage: {
      type: Boolean,
      default: true,
    },
    canDeleteMembers: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:modelValue", "updated", "deleted"],
  setup(props, { emit }) {
    const router = useRouter();
    const shioriStore = useShioriStore();
    const shioriMemberStore = useShioriMemberStore();
    const scheduleStore = useScheduleStore();
    const scheduleMemberStore = useScheduleMemberStore();
    const checklistDetailStore = useChecklistDetailStore();
    const authStore = useAuthStore();
    const destMasterStore = useDestinationMasterStore();
    onMounted(() => destMasterStore.fetchAll());
    const showShareModal = ref(false);
    const formData = ref({
      title: "",
      startDate: "",
      endDate: "",
      budget: null,
      destinationType: "domestic",
      destinations: [
        {
          value: "",
          showSuggestions: false,
          filteredList: [],
        },
      ],
      members: [],
    });
    const originalMembers = ref([]); // 元のメンバーデータを保持

    const errors = ref({
      title: "",
      startDate: "",
      endDate: "",
      destinations: "",
      members: "",
      image: "",
    });

    const errorMessage = ref("");
    const successMessage = ref("");
    const isSubmitting = ref(false);

    const imageInput = ref(null);
    const selectedImage = ref(null);
    const imagePreview = ref(null);
    const currentImageUrl = ref(null);
    let imageRequestVersion = 0;
    const isFriendListOpen = ref(false);
    const friendSearch = ref("");
    const friendCandidates = ref([]);

    const releaseCurrentImageUrl = () => {
      revokeShioriImageUrl(currentImageUrl.value);
      currentImageUrl.value = null;
    };

    const loadCurrentImage = async ({ force = false } = {}) => {
      const requestVersion = ++imageRequestVersion;
      releaseCurrentImageUrl();
      if (!force && props.shioriData?.HAS_IMAGE !== true) return;

      const embeddedImageUrl = resolveEmbeddedShioriImageUrl({
        ...props.shioriData,
        HAS_IMAGE: true,
      });
      if (embeddedImageUrl) {
        currentImageUrl.value = embeddedImageUrl;
        return;
      }

      try {
        const nextImageUrl = await fetchAuthenticatedShioriImageUrl(
          props.shioriData?.SHIORI_NOV,
        );
        if (requestVersion !== imageRequestVersion) {
          revokeShioriImageUrl(nextImageUrl);
          return;
        }
        currentImageUrl.value = nextImageUrl || null;
      } catch {
        if (requestVersion === imageRequestVersion) {
          currentImageUrl.value = null;
        }
      }
    };

    const openImagePicker = () => {
      if (!props.canWrite) return;
      imageInput.value?.click();
    };

    const handleImageSelect = (event) => {
      if (!props.canWrite) return;
      const file = event.target.files[0];
      if (!file) return;

      if (file.size > 25 * 1024 * 1024) {
        errors.value.image = "画像サイズは25MB以下にしてください";
        event.target.value = "";
        return;
      }

      const extension = file.name.toLowerCase().split(".").pop();
      const supportedExtensions = [
        "jpg",
        "jpeg",
        "png",
        "gif",
        "webp",
        "heic",
        "heif",
        "hif",
      ];
      const supportedTypes = [
        "image/jpeg",
        "image/png",
        "image/gif",
        "image/webp",
        "image/heic",
        "image/heif",
        "image/heic-sequence",
        "image/heif-sequence",
      ];
      const hasSupportedExtension = supportedExtensions.includes(extension);
      const hasSupportedType = supportedTypes.includes(
        file.type.toLowerCase(),
      );
      if (!hasSupportedExtension && !hasSupportedType) {
        errors.value.image =
          "JPEG、PNG、GIF、WebP、HEIC、HEIF形式を選択してください";
        event.target.value = "";
        return;
      }

      selectedImage.value = file;
      errors.value.image = "";

      const reader = new FileReader();
      reader.onload = (e) => {
        imagePreview.value = e.target.result;
      };
      reader.readAsDataURL(file);
    };

    const removeImage = () => {
      selectedImage.value = null;
      imagePreview.value = null;
      if (imageInput.value) {
        imageInput.value.value = "";
      }
    };

    const handleUploadImage = async () => {
      if (!props.canWrite) return;
      if (!selectedImage.value) return;

      isSubmitting.value = true;
      errorMessage.value = "";

      try {
        await shioriStore.uploadShioriImage(
          props.shioriData.SHIORI_NOV,
          selectedImage.value,
        );
        successMessage.value = "画像をアップロードしました";

        await loadCurrentImage({ force: true });
        removeImage();

        emit("updated");
      } catch (error) {
        console.error("画像アップロードエラー:", error);
        errorMessage.value =
          error.response?.data?.detail ||
          error.message ||
          "画像のアップロードに失敗しました";
      } finally {
        isSubmitting.value = false;
      }
    };

    const handleDeleteImage = async () => {
      if (!props.canWrite) return;
      if (!confirm("画像を削除しますか？")) return;

      isSubmitting.value = true;
      errorMessage.value = "";

      try {
        await shioriStore.deleteShioriImage(props.shioriData.SHIORI_NOV);
        successMessage.value = "画像を削除しました";
        imageRequestVersion += 1;
        releaseCurrentImageUrl();

        emit("updated");
      } catch (error) {
        console.error("画像削除エラー:", error);
        errorMessage.value = "画像の削除に失敗しました";
      } finally {
        isSubmitting.value = false;
      }
    };

    const addMember = () => {
      if (!props.canManage && !props.isCopyMode) return;
      if (formData.value.members.length < 20) {
        formData.value.members.push({
          member_code: null, // 新規メンバーはnull
          member_name: "",
          app_user_code: null,
        });
      }
    };

    const fetchFriendCandidates = async () => {
      try {
        friendCandidates.value =
          await shioriMemberStore.fetchFriendCandidates(friendSearch.value);
      } catch (error) {
        console.error("友達リスト取得エラー:", error);
        friendCandidates.value = [];
      }
    };

    const toggleFriendList = async () => {
      if (!props.canManage && !props.isCopyMode) return;
      isFriendListOpen.value = !isFriendListOpen.value;
      if (isFriendListOpen.value && friendCandidates.value.length === 0) {
        await fetchFriendCandidates();
      }
    };

    const isFriendSelected = (friend) =>
      formData.value.members.some(
        (member) => member.app_user_code === friend.APP_USER_CODE,
      );

    const addFriendMember = (friend) => {
      if (!props.canManage && !props.isCopyMode) return;
      if (isFriendSelected(friend) || formData.value.members.length >= 20) {
        return;
      }
      formData.value.members.push({
        member_code: null,
        member_name: friend.USER_NAME || friend.EMAIL,
        app_user_code: friend.APP_USER_CODE,
      });
    };

    // 自分自身かどうかを判定する関数
    const isCurrentUser = (member) => {
      const currentUserCode = authStore.user?.APP_USER_CODE;
      const currentUserName = authStore.user?.USER_NAME;

      // APP_USER_CODEで比較（存在する場合）
      if (currentUserCode && member.app_user_code) {
        return member.app_user_code === currentUserCode;
      }

      // 名前で比較（APP_USER_CODEがない場合）
      if (currentUserName && member.member_name) {
        return member.member_name === currentUserName;
      }

      return false;
    };

    const removeMember = async (index, memberCode) => {
      const canRemoveExistingMember =
        props.canDeleteMembers || props.isCopyMode;
      const canRemoveUnsavedMember = props.canManage || props.isCopyMode;
      if (
        (memberCode && !canRemoveExistingMember) ||
        (!memberCode && !canRemoveUnsavedMember)
      ) {
        return;
      }
      if (formData.value.members.length <= 1) {
        errorMessage.value = "メンバーは最低1人必要です";
        return;
      }

      // 自分自身は削除できない
      const member = formData.value.members[index];
      if (isCurrentUser(member)) {
        errorMessage.value = "自分自身をメンバーから削除することはできません";
        return;
      }

      // 既存のメンバーを削除する場合はAPIを呼ぶ
      if (memberCode) {
        if (!confirm("このメンバーを削除しますか？")) return;

        isSubmitting.value = true;
        try {
          await shioriMemberStore.deleteMember(
            props.shioriData.SHIORI_NOV,
            memberCode,
          );
          formData.value.members.splice(index, 1);
          successMessage.value = "メンバーを削除しました";
        } catch (error) {
          console.error("メンバー削除エラー:", error);
          errorMessage.value = "メンバーの削除に失敗しました";
        } finally {
          isSubmitting.value = false;
        }
      } else {
        // 新規追加されたがまだ保存されていないメンバーは単純に削除
        formData.value.members.splice(index, 1);
      }
    };

    // 渡航先関連の関数
    const addDestination = () => {
      if (formData.value.destinations.length < 10) {
        formData.value.destinations.push({
          value: "",
          showSuggestions: false,
          filteredList: [],
        });
      }
    };

    const removeDestination = (index) => {
      if (formData.value.destinations.length > 1) {
        formData.value.destinations.splice(index, 1);
      }
    };

    const handleDestinationInput = (index) => {
      const destination = formData.value.destinations[index];
      const inputValue = destination.value;
      destination.filteredList = filterDestinationSuggestions({
        inputValue,
        destinationType: formData.value.destinationType,
        prefectureNames: destMasterStore.prefectureNames,
        countryRows: destMasterStore.countries,
        countryAliasRows: destMasterStore.countryAliases,
      });
    };

    const showSuggestions = (index) => {
      formData.value.destinations[index].showSuggestions = true;
      handleDestinationInput(index);
    };

    const hideSuggestions = (index) => {
      setTimeout(() => {
        formData.value.destinations[index].showSuggestions = false;
      }, 200);
    };

    const selectDestination = (index, suggestion) => {
      formData.value.destinations[index].value = suggestion;
      formData.value.destinations[index].showSuggestions = false;
      formData.value.destinations[index].filteredList = [];
    };

    const closeModal = () => {
      emit("update:modelValue", false);
      clearMessages();
    };

    const clearMessages = () => {
      errors.value = {
        title: "",
        startDate: "",
        endDate: "",
        destinations: "",
        members: "",
        image: "",
      };
      errorMessage.value = "";
      successMessage.value = "";
    };

    const validateYear = (fieldName) => {
      const dateValue = formData.value[fieldName];
      if (!dateValue) return;

      // 年の部分を抽出して検証（YYYY-MM-DD形式）
      const yearMatch = dateValue.match(/^(\d+)-/);
      if (yearMatch) {
        const year = yearMatch[1];

        // 年が3桁以下の場合は編集中と判断して何もしない
        if (year.length < 4) {
          return;
        }

        // 年が4桁を超える場合のみ、最初の4桁に切り詰め
        if (year.length > 4) {
          const validYear = year.substring(0, 4);
          const [, month, day] = dateValue.split("-");
          formData.value[fieldName] = `${validYear}-${month || "01"}-${
            day || "01"
          }`;
          return;
        }

        // 年が正確に4桁の場合のみ範囲チェック
        if (year.length === 4) {
          const yearNum = parseInt(year);
          if (yearNum < 1900) {
            formData.value[fieldName] = `1900-${
              dateValue.split("-")[1] || "01"
            }-${dateValue.split("-")[2] || "01"}`;
          } else if (yearNum > 9999) {
            formData.value[fieldName] = `9999-${
              dateValue.split("-")[1] || "12"
            }-${dateValue.split("-")[2] || "31"}`;
          }
        }
      }
    };

    const validateForm = () => {
      clearMessages();
      let isValid = true;

      if (!formData.value.title || !formData.value.title.trim()) {
        errors.value.title = "タイトルは必須です";
        isValid = false;
      } else if (formData.value.title.length > 255) {
        errors.value.title = "タイトルは255文字以内で入力してください";
        isValid = false;
      }

      if (!formData.value.startDate) {
        errors.value.startDate = "開始日は必須です";
        isValid = false;
      }

      if (!formData.value.endDate) {
        errors.value.endDate = "終了日は必須です";
        isValid = false;
      } else if (formData.value.startDate && formData.value.endDate) {
        const start = new Date(formData.value.startDate);
        const end = new Date(formData.value.endDate);
        if (end < start) {
          errors.value.endDate = "終了日は開始日以降の日付を指定してください";
          isValid = false;
        }
      }

      // メンバーのバリデーション
      if (!formData.value.members || formData.value.members.length === 0) {
        errors.value.members = "メンバーを最低1人追加してください";
        isValid = false;
      } else {
        const emptyMembers = formData.value.members.filter(
          (m) => !m.member_name || !m.member_name.trim(),
        );
        if (emptyMembers.length > 0) {
          errors.value.members = "メンバー名を入力してください";
          isValid = false;
        }
      }

      // 渡航先のバリデーション
      if (
        !formData.value.destinations ||
        formData.value.destinations.length === 0
      ) {
        errors.value.destinations = "旅行先を最低1つ追加してください";
        isValid = false;
      } else {
        const emptyDestinations = formData.value.destinations.filter(
          (d) => !d.value || !d.value.trim(),
        );
        if (emptyDestinations.length > 0) {
          errors.value.destinations = "旅行先を入力してください";
          isValid = false;
        }
      }

      return isValid;
    };

    const handleUpdate = async () => {
      if (!props.canWrite && !props.isCopyMode) return;
      if (!validateForm()) {
        errorMessage.value = "入力内容に誤りがあります。修正してください。";
        return;
      }

      isSubmitting.value = true;
      errorMessage.value = "";
      successMessage.value = "";

      try {
        if (props.isCopyMode) {
          // タイトル重複チェック
          const token = localStorage.getItem("token");
          const checkResponse = await axios.get(
            `${API_URL}/shiori/check-title/${encodeURIComponent(
              formData.value.title,
            )}`,
            { headers: { Authorization: `Bearer ${token}` } },
          );

          if (checkResponse.data.exists) {
            errorMessage.value =
              "同じ名前のしおりが既に存在します。別の名前を入力してください。";
            isSubmitting.value = false;
            return;
          }

          // コピーモード: 新規しおりを作成
          // 渡航先データを整形
          const destinationsData = formData.value.destinations
            .filter((d) => d.value && d.value.trim())
            .map((d) => d.value.trim());

          const newShiori = await shioriStore.createShiori({
            title: formData.value.title,
            startDate: formData.value.startDate,
            endDate: formData.value.endDate,
            destinationType: formData.value.destinationType,
            destinations: destinationsData,
          });

          const newShioriId = newShiori.shiori_id;
          const originalShioriId = props.shioriData.SHIORI_NOV;

          // 現在のログインユーザー情報を取得
          const currentUserName =
            authStore.user?.user_name || authStore.user?.USER_NAME;
          const currentUserCode =
            authStore.user?.APP_USER_CODE || authStore.user?.user_id;

          // メンバーを追加（member_codeとmember_nameのマッピングを保持）
          const memberMapping = {}; // 旧member_code -> 新member_code
          for (const member of formData.value.members) {
            if (member.member_name && member.member_name.trim()) {
              // ログインユーザーの場合はAPP_USER_CODEを設定
              const isCurrentUser =
                member.member_name.trim() === currentUserName;
              const newMember = await shioriMemberStore.createMember(
                newShioriId,
                {
                  member_name: member.member_name.trim(),
                  app_user_code:
                    member.app_user_code ||
                    (isCurrentUser ? currentUserCode : null),
                },
              );
              // 旧member_codeと新member_codeをマッピング
              memberMapping[member.member_code] = newMember.member_code;
            }
          }

          // スケジュールを複製
          await scheduleStore.fetchSchedules(originalShioriId);
          const originalSchedules = scheduleStore.schedules;

          console.log("[複製] 元のスケジュール数:", originalSchedules.length);

          for (const sched of originalSchedules) {
            // タイトルが空の場合はスキップ
            const scheduleTitle = sched.SUBJECT_NMV || sched.SCHEDULE_NMV;
            if (!scheduleTitle || !scheduleTitle.trim()) {
              console.log(
                "[複製] タイトルなしのスケジュールをスキップ:",
                sched,
              );
              continue;
            }

            try {
              // 日付をISO形式の文字列に変換
              const startDate = sched.START_YMD || sched.START_DATE;
              const endDate = sched.END_YMD || sched.END_DATE || startDate;

              const scheduleData = {
                shioriId: newShioriId,
                startDate:
                  typeof startDate === "string"
                    ? startDate
                    : startDate?.toISOString?.()?.split("T")[0],
                endDate:
                  typeof endDate === "string"
                    ? endDate
                    : endDate?.toISOString?.()?.split("T")[0],
                startTime: sched.START_TIME || null,
                endTime: sched.END_TIME || null,
                title: scheduleTitle.trim(),
                remarks: sched.REMARKS || sched.MEMO || "",
                address: sched.ADDRESS_NMV || sched.PLACE || "",
                googlePlaceNmv:
                  sched.GOOGLE_PLACE_NMV || sched.googlePlaceNmv || null,
                googlePlaceId:
                  sched.GOOGLE_PLACE_ID || sched.googlePlaceId || null,
                cost: sched.COST || 0,
                currency: sched.CURRENCY || "JPY",
                transportation: sched.CATEGORY_CDV || "",
                tel: sched.TEL_AREA_CODE || "",
                url: sched.URL_NMV || "",
                reservation: sched.RESERVATION_FLG === "1",
                needFlg: sched.NEED_FLG !== "0",
                reservationDone: sched.RESERVATION_DONE_FLG === "1",
                timeDifference: String(sched.TIME_DIFFERENCE_NUM || 9),
              };

              console.log("[複製] スケジュール送信データ:", scheduleData);
              const newSchedule = await scheduleStore.createSchedule(
                scheduleData,
              );

              // スケジュールメンバーを全員追加
              const allMemberCodes = Object.values(memberMapping);
              for (const memberCode of allMemberCodes) {
                await scheduleMemberStore.createScheduleMember({
                  shiori_id: newShioriId,
                  schedule_id: newSchedule.schedule_id,
                  member_code: memberCode,
                  app_user_code: null,
                  paid_amount: 0,
                });
              }
              console.log("[複製] スケジュール複製成功:", scheduleTitle);
            } catch (schedError) {
              console.error("[複製] スケジュール複製エラー:", schedError);
              console.error("[複製] 問題のスケジュールデータ:", sched);
              // エラーがあってもスキップして続行
            }
          }

          // チェックリストを複製
          await checklistDetailStore.fetchChecklistDetails(originalShioriId);
          const originalChecklists = checklistDetailStore.checklistDetails;

          console.log(
            "[複製] 元のチェックリスト数:",
            originalChecklists.length,
          );

          for (const item of originalChecklists) {
            try {
              await checklistDetailStore.createChecklistDetail({
                shiori_id: newShioriId,
                category_name: item.CATEGORY_NMV || "その他",
                item_name: item.SUBJECT_NMV || "未設定",
              });
              console.log("[複製] チェックリスト複製成功:", item.SUBJECT_NMV);
            } catch (checkError) {
              console.error("[複製] チェックリスト複製エラー:", checkError);
              console.error("[複製] 問題のチェックリストデータ:", item);
              // エラーがあってもスキップして続行
            }
          }

          successMessage.value = "しおりを複製しました";
          emit("updated");

          // 複製したしおりの詳細ページに遷移
          setTimeout(() => {
            closeModal();
            router.push(`/shiori/${newShioriId}`);
          }, 800);
        } else {
          // 編集モード: タイトルが変更されている場合は重複チェック
          if (
            !isGuestMode() &&
            formData.value.title !== props.shioriData.SUBJECT_NMV
          ) {
            const token = localStorage.getItem("token");
            const checkResponse = await axios.get(
              `${API_URL}/shiori/check-title-exclude/${encodeURIComponent(
                formData.value.title,
              )}/${props.shioriData.SHIORI_NOV}`,
              { headers: { Authorization: `Bearer ${token}` } },
            );

            if (checkResponse.data.exists) {
              errorMessage.value =
                "同じ名前のしおりが既に存在します。別の名前を入力してください。";
              isSubmitting.value = false;
              return;
            }
          }

          // 編集モード: しおりを更新
          // 渡航先データを整形
          const destinationsData = formData.value.destinations
            .filter((d) => d.value && d.value.trim())
            .map((d) => d.value.trim());

          await shioriStore.updateShiori(props.shioriData.SHIORI_NOV, {
            title: formData.value.title,
            startDate: formData.value.startDate,
            endDate: formData.value.endDate,
            destinationType: formData.value.destinationType,
            destinations: destinationsData,
            budget: formData.value.budget,
          });

          // 現在のログインユーザー情報を取得
          const currentUserName =
            authStore.user?.user_name || authStore.user?.USER_NAME;
          const currentUserCode =
            authStore.user?.APP_USER_CODE || authStore.user?.user_id;

          // メンバーの更新処理（共有管理権限がある場合のみ）
          for (const member of props.canManage ? formData.value.members : []) {
            if (member.member_code) {
              // 既存メンバーの更新
              const originalMember = originalMembers.value.find(
                (m) => m.MEMBER_CODE === member.member_code,
              );
              if (
                originalMember &&
                originalMember.MEMBER_NAME !== member.member_name
              ) {
                await shioriMemberStore.updateMember(
                  props.shioriData.SHIORI_NOV,
                  member.member_code,
                  { member_name: member.member_name },
                );
              }
            } else {
              // 新規メンバーの追加
              if (member.member_name && member.member_name.trim()) {
                // ログインユーザーの場合はAPP_USER_CODEを設定
                const isCurrentUser =
                  member.member_name.trim() === currentUserName;
                await shioriMemberStore.createMember(
                  props.shioriData.SHIORI_NOV,
                  {
                    member_name: member.member_name.trim(),
                    app_user_code:
                      member.app_user_code ||
                      (isCurrentUser ? currentUserCode : null),
                  },
                );
              }
            }
          }

          // メンバー情報を再取得してストアを更新
          if (props.canManage) {
            await shioriMemberStore.fetchMembers(props.shioriData.SHIORI_NOV);
          }

          // 選択中の画像があれば自動アップロード
          if (selectedImage.value) {
            try {
              await shioriStore.uploadShioriImage(
                props.shioriData.SHIORI_NOV,
                selectedImage.value,
              );
              await loadCurrentImage({ force: true });
              removeImage();
            } catch (imgErr) {
              console.error("画像アップロードエラー:", imgErr);
              errorMessage.value =
                imgErr.response?.data?.detail ||
                imgErr.message ||
                "しおりは更新されましたが、画像アップロードに失敗しました。";
              isSubmitting.value = false;
              return;
            }
          }

          successMessage.value = "しおりを更新しました";
          emit("updated");

          setTimeout(() => {
            closeModal();
          }, 1000);
        }
      } catch (error) {
        console.error(
          props.isCopyMode ? "しおり複製エラー:" : "しおり更新エラー:",
          error,
        );
        if (error.response?.data?.detail) {
          errorMessage.value = error.response.data.detail;
        } else {
          errorMessage.value = props.isCopyMode
            ? "しおりの複製に失敗しました。"
            : "しおりの更新に失敗しました。";
        }
      } finally {
        isSubmitting.value = false;
      }
    };

    const handleDelete = async () => {
      if (!props.canManage || !props.canDelete) return;
      if (
        !confirm(
          "このしおりを削除してもよろしいですか？\nこの操作は取り消せません。",
        )
      ) {
        return;
      }

      isSubmitting.value = true;
      errorMessage.value = "";

      try {
        await shioriStore.deleteShiori(props.shioriData.SHIORI_NOV);
        emit("deleted");
        closeModal();
      } catch (error) {
        console.error("しおり削除エラー:", error);
        if (error.response?.data?.detail) {
          errorMessage.value = error.response.data.detail;
        } else {
          errorMessage.value = "しおりの削除に失敗しました。";
        }
      } finally {
        isSubmitting.value = false;
      }
    };

    // モーダルが開かれた時にフォームデータを初期化
    watch(
      () => props.modelValue,
      async (newValue) => {
        if (newValue && props.shioriData) {
          isFriendListOpen.value = false;
          friendSearch.value = "";
          friendCandidates.value = [];
          // 渡航先データを読み込み
          let destinationType = "domestic";
          let destinationsArray = [];

          try {
            if (props.shioriData.DESTINATION_TYPE) {
              destinationType = props.shioriData.DESTINATION_TYPE;
            }
            if (props.shioriData.DESTINATIONS) {
              const parsedDestinations = JSON.parse(
                props.shioriData.DESTINATIONS,
              );
              destinationsArray = parsedDestinations.map((d) => ({
                value: d,
                showSuggestions: false,
                filteredList: [],
              }));
            }
          } catch (error) {
            console.error("渡航先データの読み込みエラー:", error);
          }

          // 渡航先がない場合は空の入力欄を1つ追加
          if (destinationsArray.length === 0) {
            destinationsArray.push({
              value: "",
              showSuggestions: false,
              filteredList: [],
            });
          }

          formData.value = {
            title: props.shioriData.SUBJECT_NMV || "",
            startDate: props.shioriData.TRIP_FROM
              ? new Date(props.shioriData.TRIP_FROM).toISOString().split("T")[0]
              : "",
            endDate: props.shioriData.TRIP_TO
              ? new Date(props.shioriData.TRIP_TO).toISOString().split("T")[0]
              : "",
            budget: props.shioriData.BUDGET ? Number(props.shioriData.BUDGET) : null,
            members: [],
            destinationType: destinationType,
            destinations: destinationsArray,
          };

          // 編集データの日付を検証
          if (formData.value.startDate) {
            validateYear("startDate");
          }
          if (formData.value.endDate) {
            validateYear("endDate");
          }

          // メンバーデータを取得
          try {
            const members = await shioriMemberStore.fetchMembers(
              props.shioriData.SHIORI_NOV,
            );
            originalMembers.value = members;
            formData.value.members = members.map((m) => ({
              member_code: m.MEMBER_CODE,
              member_name: m.MEMBER_NAME,
              app_user_code: m.APP_USER_CODE,
            }));

            // メンバーがいない場合は空のメンバーを1つ追加
            if (formData.value.members.length === 0) {
              formData.value.members.push({
                member_code: null,
                member_name: "",
                app_user_code: null,
              });
            }
          } catch (error) {
            console.error("メンバー取得エラー:", error);
            // エラーの場合は空のメンバーを1つ追加
            formData.value.members = [
              {
                member_code: null,
                member_name: "",
                app_user_code: null,
              },
            ];
          }

          await loadCurrentImage();

          clearMessages();
          removeImage();
        } else {
          imageRequestVersion += 1;
          releaseCurrentImageUrl();
        }
      },
    );

    onUnmounted(() => {
      imageRequestVersion += 1;
      releaseCurrentImageUrl();
    });

    const openShareModal = () => {
      if (!props.canManage) return;
      showShareModal.value = true;
    };

    return {
      formData,
      errors,
      errorMessage,
      successMessage,
      isSubmitting,
      showShareModal,
      imageInput,
      selectedImage,
      imagePreview,
      currentImageUrl,
      closeModal,
      handleUpdate,
      handleDelete,
      handleImageSelect,
      handleUploadImage,
      handleDeleteImage,
      removeImage,
      openImagePicker,
      openShareModal,
      isFriendListOpen,
      friendSearch,
      friendCandidates,
      fetchFriendCandidates,
      toggleFriendList,
      isFriendSelected,
      addFriendMember,
      addMember,
      removeMember,
      isCurrentUser,
      addDestination,
      removeDestination,
      handleDestinationInput,
      showSuggestions,
      hideSuggestions,
      selectDestination,
      validateYear,
    };
  },
};
</script>

<style scoped>
/* モーダルオーバーレイ */
.modal-overlay {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 200;
  padding: 1rem;
  padding-top: 5rem;
  overflow-y: auto;
}

/* モーダルコンテナ */
.modal-container {
  background: white;
  border-radius: var(--radius-xl);
  width: 100%;
  max-width: 28rem;
  max-height: calc(100dvh - 6rem);
  box-shadow: var(--shadow-xl);
  animation: modal-slide-up 0.3s ease-out;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

@keyframes modal-slide-up {
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* モーダルヘッダー */
.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem 1.5rem 1rem;
  border-bottom: 1px solid var(--color-border-light);
}

.modal-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--color-text);
  margin: 0;
}

.modal-close-btn {
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  color: var(--color-text-muted);
  font-size: 1.75rem;
  line-height: 1;
  cursor: pointer;
  border-radius: var(--radius-md);
  transition: all var(--transition-base);
}

.modal-close-btn:hover {
  background-color: var(--color-cool-bg);
  color: var(--color-text);
}

/* フォーム */
.modal-form {
  padding: 1.5rem;
}

.form-group {
  margin-bottom: 1.25rem;
}

.form-label {
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--color-text);
  margin-bottom: 0.5rem;
}

.required {
  color: #ef4444;
}

.form-input {
  width: 100%;
  padding: 0.625rem 0.875rem;
  font-size: 0.9375rem;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  transition: all var(--transition-base);
  background-color: white;
}

.form-input:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-input:hover:not(:focus) {
  border-color: var(--color-text-light);
}

.input-error {
  border-color: #ef4444 !important;
}

.error-text {
  font-size: 0.75rem;
  color: #ef4444;
  margin-top: 0.375rem;
}

/* エラーメッセージ */
.error-message {
  margin: 1.5rem 1.5rem 0;
  padding: 0.875rem 1rem;
  background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
  border-left: 3px solid #ef4444;
  border-radius: var(--radius-md);
  color: #991b1b;
  font-size: 0.875rem;
  font-weight: 500;
}

/* 成功メッセージ */
.success-message {
  margin: 1.5rem 1.5rem 0;
  padding: 0.875rem 1rem;
  background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
  border-left: 3px solid #10b981;
  border-radius: var(--radius-md);
  color: #065f46;
  font-size: 0.875rem;
  font-weight: 500;
}

/* 画像アップロードエリア */
.image-upload-area {
  position: relative;
  width: 100%;
}

.image-input {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
}

.image-upload-label {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem 1rem;
  border: 2px dashed var(--color-border);
  border-radius: var(--radius-md);
  background-color: var(--color-background);
  cursor: pointer;
  transition: all var(--transition-base);
}

.image-upload-label:hover {
  border-color: var(--primary-color);
  background-color: rgba(59, 130, 246, 0.05);
}

.upload-icon {
  font-size: 2.5rem;
  margin-bottom: 0.5rem;
}

.upload-text {
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--color-text);
  margin-bottom: 0.25rem;
}

.upload-hint {
  font-size: 0.75rem;
  color: var(--color-text-muted);
}

.image-preview-container {
  position: relative;
  width: 100%;
  border-radius: var(--radius-md);
  overflow: hidden;
}

.image-preview {
  width: 100%;
  height: auto;
  max-height: 300px;
  object-fit: cover;
  display: block;
}

.remove-image-btn {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.6);
  color: white;
  border: none;
  border-radius: 50%;
  font-size: 1.5rem;
  line-height: 1;
  cursor: pointer;
  transition: all var(--transition-base);
  backdrop-filter: blur(4px);
}

.remove-image-btn:hover {
  background-color: rgba(239, 68, 68, 0.9);
  transform: scale(1.1);
}

.change-image-btn {
  position: absolute;
  left: 0.75rem;
  bottom: 0.75rem;
  padding: 0.45rem 0.75rem;
  border: none;
  border-radius: var(--radius-full);
  background: rgba(0, 0, 0, 0.68);
  color: white;
  font-size: 0.8125rem;
  font-weight: 700;
  cursor: pointer;
  backdrop-filter: blur(4px);
}

.change-image-btn:hover:not(:disabled) {
  background: rgba(0, 0, 0, 0.82);
}

.change-image-btn:disabled {
  opacity: 0.6;
  cursor: wait;
}

/* メンバー入力 */
.member-add-tools {
  display: flex;
  margin-bottom: 0.75rem;
}

.friend-list-toggle {
  display: inline-flex;
  gap: 0.45rem;
  align-items: center;
  justify-content: center;
  padding: 0.55rem 0.8rem;
  border: 1px solid var(--primary-color);
  border-radius: var(--radius-md);
  background: white;
  color: var(--primary-color);
  font-size: 0.875rem;
  font-weight: 700;
  cursor: pointer;
}

.friend-picker {
  display: grid;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
  padding: 0.75rem;
  border: 1px solid var(--color-border-light);
  border-radius: var(--radius-md);
  background: white;
}

.friend-search-input {
  font-size: 1rem;
}

.friend-option {
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 0.65rem;
  align-items: center;
  width: 100%;
  padding: 0.55rem;
  border: 1px solid var(--color-border-light);
  border-radius: var(--radius-md);
  background: var(--color-background);
  color: var(--color-text);
  text-align: left;
  cursor: pointer;
}

.friend-option:disabled {
  opacity: 0.58;
  cursor: not-allowed;
}

.friend-avatar {
  display: grid;
  width: 2rem;
  height: 2rem;
  place-items: center;
  overflow: hidden;
  border-radius: 50%;
  background: #e5e7eb;
  color: var(--color-text-muted);
}

.friend-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.friend-option strong,
.friend-option small {
  display: block;
}

.friend-option small {
  color: var(--color-text-muted);
  font-size: 0.75rem;
}

.friend-add-state {
  color: var(--primary-color);
  font-size: 0.75rem;
  font-weight: 700;
}

.friend-empty {
  padding: 0.75rem;
  color: var(--color-text-muted);
  font-size: 0.8125rem;
  text-align: center;
}

.members-input-container {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.member-input-row {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.member-input {
  flex: 1;
}

.remove-member-btn {
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #ef4444;
  color: white;
  border: none;
  border-radius: var(--radius-sm);
  font-size: 1.5rem;
  font-weight: bold;
  line-height: 1;
  cursor: pointer;
  transition: all var(--transition-base);
  flex-shrink: 0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.remove-member-btn:hover {
  background-color: #dc2626;
  transform: scale(1.1);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.15);
}

.remove-member-btn:disabled {
  background-color: #9ca3af;
  cursor: not-allowed;
  opacity: 0.6;
}

.add-member-btn {
  padding: 0.625rem 1rem;
  background-color: var(--color-background);
  color: var(--primary-color);
  border: 1px dashed var(--primary-color);
  border-radius: var(--radius-sm);
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all var(--transition-base);
  text-align: center;
}

.add-member-btn:hover:not(:disabled) {
  background-color: rgba(59, 130, 246, 0.1);
  border-style: solid;
}

.add-member-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-upload {
  margin-top: 0.75rem;
  width: 100%;
  padding: 0.5rem 1rem;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  border: none;
  border-radius: var(--radius-md);
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all var(--transition-base);
}

.btn-upload:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
}

.btn-upload:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* モーダルフッター */
.modal-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1.75rem;
  padding-top: 1.25rem;
  border-top: 1px solid var(--color-border-light);
}

.button-group {
  display: flex;
  gap: 0.75rem;
}

/* 削除ボタン */
.btn-danger {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
  box-shadow: var(--shadow-sm);
}

.btn-danger:hover:not(:disabled) {
  box-shadow: var(--shadow-md);
  transform: translateY(-1px);
}

/* 共有ボタン */
.btn-share {
  background: linear-gradient(135deg, #b4cf9e 0%, #98b678 100%);
  color: white;
  box-shadow: var(--shadow-sm);
}

.btn-share:hover:not(:disabled) {
  background: linear-gradient(135deg, #98b678 0%, #88a46a 100%);
  box-shadow: var(--shadow-md);
  transform: translateY(-1px);
}

/* トランジション */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter-active .modal-container {
  animation: modal-slide-up 0.3s ease-out;
}

.modal-fade-leave-active .modal-container {
  animation: modal-slide-down 0.3s ease-out;
}

@keyframes modal-slide-down {
  from {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
  to {
    opacity: 0;
    transform: translateY(20px) scale(0.95);
  }
}

/* 渡航先選択スタイル */
.destination-type-selector {
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
}

.destination-type-btn {
  flex: 1;
  padding: 0.75rem;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  background: white;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.destination-type-btn:hover {
  border-color: #b4cf9e;
  background: #f7fafc;
}

.destination-type-btn.active {
  border-color: #b4cf9e;
  background: #b4cf9e;
  color: white;
}

.destinations-container {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.destination-input-row {
  display: flex;
  gap: 0.5rem;
  align-items: start;
}

.destination-autocomplete {
  position: relative;
  flex: 1;
}

.suggestions-list {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  max-height: 200px;
  overflow-y: auto;
  background: white;
  border: 1px solid #e2e8f0;
  border-top: none;
  border-radius: 0 0 8px 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  z-index: 10;
}

.suggestion-item {
  padding: 0.75rem 1rem;
  cursor: pointer;
  transition: background 0.2s;
}

.suggestion-item:hover {
  background: #f7fafc;
}

/* レスポンシブ */
/* モバイル (～640px) */
@media (max-width: 640px) {
  .modal-overlay {
    padding: 0.5rem;
    align-items: flex-start;
  }

  .modal-container {
    max-width: 100%;
    width: 100%;
    max-height: calc(100dvh - 1rem);
    margin: 0;
  }

  .modal-header {
    padding: 1rem;
  }

  .modal-title {
    font-size: 1.125rem;
  }

  .close-button {
    width: 32px;
    height: 32px;
    font-size: 1.5rem;
  }

  .modal-body {
    padding: 1rem;
  }

  .form-section + .form-section {
    margin-top: 1.25rem;
  }

  .section-title {
    font-size: 0.9375rem;
    margin-bottom: 0.75rem;
  }

  .form-group {
    margin-bottom: 1rem;
  }

  .form-label {
    font-size: 0.8125rem;
  }

  input[type="text"],
  input[type="date"],
  textarea {
    padding: 0.625rem 0.875rem;
    font-size: 0.875rem;
  }

  .member-input-row {
    gap: 0.5rem;
  }

  .member-item {
    padding: 0.75rem;
  }

  .member-name {
    font-size: 0.875rem;
  }

  .destination-type-selector {
    gap: 0.5rem;
  }

  .destination-type-btn {
    padding: 0.625rem 1rem;
    font-size: 0.875rem;
  }

  .suggestion-item {
    padding: 0.625rem 0.875rem;
    font-size: 0.875rem;
  }

  .upload-icon {
    font-size: 2rem;
  }

  .upload-text {
    font-size: 0.875rem;
  }

  .upload-hint {
    font-size: 0.6875rem;
  }

  .modal-footer {
    gap: 0.5rem;
    margin-top: 1.25rem;
    padding-top: 1rem;
  }

  .modal-footer .btn {
    font-size: 0.875rem;
    padding: 0.625rem 1rem;
  }
}

/* タブレット (641px～1024px) */
@media (min-width: 641px) and (max-width: 1024px) {
  .modal-container {
    max-width: 600px;
  }
}
</style>
